import { HttpClient } from '@angular/common/http';
import {
    EntityActionFactory,
    EntityCacheDispatcher, EntityOp,
    HttpUrlGenerator, Logger, MergeStrategy, QueryParams
} from '@ngrx/data';
import { Observable, Subscription } from 'rxjs/index';
import { map, withLatestFrom } from 'rxjs/operators';
import { WebsocketBrokerService } from '../../../core/services/websocket-broker.service';
import { WebSocketEntityDataServiceBase } from '../../../state/entity/web-socket-entity-data-service-base';
import { Appointment } from '../../appointments/state/appointment.model';
import { dateAppointmentEntityMetadata } from '../../appointments/state/date-appointment.entity';
import { DateAppointmentService } from '../../appointments/state/date-appointment.service';
import { patientAppointmentEntityMetadata } from '../../patients/state/patient-appointment.entity';
import { PatientAppointmentService } from '../../patients/state/patient-appointment.service';
import { AppointmentListSelectors } from './appointment-list.selectors';

export const entityActionFactory = new EntityActionFactory();

/**
 * This is the base data service to get appointment list (called by NgRx Effects to make actuall calls to the backend).
 * This must be specialized e.g. to get AppointmentList entity data.
 */
export abstract class AppointmentListEntityDataServiceBase<T> extends WebSocketEntityDataServiceBase<any> {
    subscriptions: Subscription = new Subscription();
    pageType$: Observable<string>;

    constructor(http: HttpClient,
                httpUrlGenerator: HttpUrlGenerator,
                logger: Logger,
                public entityCacheDispatcher: EntityCacheDispatcher,
                public websocketBrokerService: WebsocketBrokerService,
                public dateAppointmentService: DateAppointmentService,
                public patientAppointmentService: PatientAppointmentService,
                entityName: string,
                public webSocketTopic: string,
                public appointmentsListSelectors: AppointmentListSelectors
                ) {
        super(entityName, http, httpUrlGenerator);
        this.pageType$ = this.appointmentsListSelectors.selectPageType();
    }

    getWithQuery(parameters: string | QueryParams): Observable<Array<any>> {
        return super.getWithQuery(parameters);
    }

    subscribeWebSocketJobWithBody(initialBody: any): void {
        // NOTE: Since we're not subscribing to the result inside an NgRx Effect we need to subscribe here and manage the
        //       subscription.
       const websocketBrokerServiceSubscription =
            this.websocketBrokerService.getRemoteObservable(this.webSocketTopic, initialBody)
                .pipe(
                    withLatestFrom(this.dateAppointmentService.entities$),
                    withLatestFrom(this.patientAppointmentService.entities$),
                    map(([[payload, appointments], patientAppointments]) => {
                        const updatedAppointments: Array<Appointment> = [];
                        let entityName;
                        this.subscriptions.add(this.pageType$.subscribe(pageType => entityName = pageType));
                        if (appointments !== undefined && appointments.length > 0 ||
                            patientAppointments !== undefined && patientAppointments.length > 0) {

                                (patientAppointments === undefined ||
                                (patientAppointments !== undefined && patientAppointments.length === 0)) ?
                                dateAppointmentEntityMetadata.entityName : patientAppointmentEntityMetadata.entityName;

                            if (entityName === dateAppointmentEntityMetadata.entityName) {
                                appointments.forEach(appointment => {
                                    if (appointment.personId === +payload.personId) {
                                        const selectAppointment: Appointment = {...appointment};
                                        selectAppointment.hasUnreadMessage = payload.hasUnreadMessage;
                                        selectAppointment.destinatorId = +payload.destinatorId;

                                        updatedAppointments.push(selectAppointment);
                                    } else {
                                        updatedAppointments.push(appointment);
                                    }
                                });
                            }

                            if (entityName === patientAppointmentEntityMetadata.entityName) {
                                patientAppointments.forEach(appointment => {
                                    if (appointment.personId === +payload.personId) {
                                        const selectAppointment: Appointment = {...appointment};
                                        selectAppointment.hasUnreadMessage = payload.hasUnreadMessage;
                                        selectAppointment.destinatorId = +payload.destinatorId;

                                        updatedAppointments.push(selectAppointment);
                                    } else {
                                        updatedAppointments.push(appointment);
                                    }
                                });
                            }

                            const entityAction = entityActionFactory.create<Array<Appointment>>(
                                entityName,
                                EntityOp.UPSERT_MANY,
                                updatedAppointments,
                                {
                                    tag: 'TwoWayUnreadEffect',
                                    mergeStrategy: MergeStrategy.OverwriteChanges
                                }
                            );

                            this.entityCacheDispatcher.dispatch(entityAction);

                        }
                    })
                )
                .subscribe();

       this.subscriptions.add(websocketBrokerServiceSubscription);
    }

    unsubscribeWebSocketJob(): void {
        this.websocketBrokerService.unsubscribe(this.webSocketTopic);
        this.subscriptions.unsubscribe();
        this.subscriptions = new Subscription();
    }

    refreshWebSocketJobWithBody(messageBody: any): void {
        this.websocketBrokerService.sendAction('refresh', this.webSocketTopic, messageBody);
    }
}
